import React, { useEffect, useState } from 'react';
import './ServiceDescription.scss';
import { useDispatch, useSelector } from 'react-redux';
import { ClickOutside } from '../../../components/ClickOutside';
import { saveCitizenship } from '../../../utils/citizenshipStorage';
import {
    getCitizenshipId,
    getHasInternationalPriceInCatalog,
    getSelectedService,
} from '../../store/selectors';
import { selectCitizenship } from '../../store/serviceSlice';

const styles = require('b_')
    .with('service-description');

const SHORT_TEXT_LENGTH = 150;
const CITIZENSHIP_OPTIONS = [
    { id: 'belarus', name: 'для граждан РБ' },
    { id: 'foreign', name: 'для иностранных граждан' },
];

const formatPrice = (price) => {
    const normalizedPrice = Number(price);

    return Number.isFinite(normalizedPrice)
        ? normalizedPrice.toFixed(2).replace('.', ',')
        : price;
};

const getPriceDisplay = ({
    belarusPrice,
    internationalPrice,
    citizenship,
    hasCatalogInternationalPrice,
}) => {
    const hasBelarusPrice = belarusPrice != null;
    const hasInternationalPrice = internationalPrice != null;
    const hasCitizenshipSelector = hasBelarusPrice && hasInternationalPrice;
    let price = belarusPrice;
    let citizenshipLabel = null;

    if (hasCitizenshipSelector && citizenship === 'foreign') {
        price = internationalPrice;
    } else if (!hasBelarusPrice && hasInternationalPrice) {
        price = internationalPrice;
    }

    if (!hasCitizenshipSelector && hasCatalogInternationalPrice) {
        if (hasInternationalPrice) {
            citizenshipLabel = CITIZENSHIP_OPTIONS.find(({ id }) => id === 'foreign').name;
        } else if (hasBelarusPrice) {
            citizenshipLabel = CITIZENSHIP_OPTIONS.find(({ id }) => id === 'belarus').name;
        }
    }

    return {
        price,
        citizenshipLabel,
        hasCitizenshipSelector,
    };
};

const CitizenshipControl = ({
    citizenship,
    citizenshipMenuOpen,
    selectedCitizenship,
    onCitizenshipSelect,
    onMenuToggle,
    onMenuClose,
}) => (
    <ClickOutside onClick={onMenuClose} open={citizenshipMenuOpen}>
        <div className={styles('citizenship')}>
            <button
                type="button"
                className={styles('citizenship-toggle')}
                onClick={onMenuToggle}>
                {selectedCitizenship?.name}
                <i className={styles('arrow', citizenshipMenuOpen ? { up: true } : { down: true })}></i>
            </button>
            {citizenshipMenuOpen && (
                <ul className={styles('citizenship-menu')}>
                    {CITIZENSHIP_OPTIONS.map(({ id, name }) => (
                        // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
                        <li
                            key={id}
                            className={styles('citizenship-option', { selected: id === citizenship })}
                            onClick={() => onCitizenshipSelect(id)}>
                            {name}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    </ClickOutside>
);

const ServicePrice = ({ price, citizenshipLabel }) => (price != null ? (
    <>
        <div className={styles('price')}>
            {formatPrice(price)} <span className="nbrb-icon currency-symbol">BYN</span>
            {citizenshipLabel && (
                <span className={styles('citizenship-label')}>
                    {citizenshipLabel}
                </span>
            )}
        </div>
        <div className={styles('price-note')}>
            * Расходные материалы и дополнительные услуги оплачиваются отдельно.
        </div>
    </>
) : null);

export const ServiceDescription = ({ selectedService: _selectedService }) => {
    const [ partialText, setPartialText ] = useState(true);
    const citizenship = useSelector(getCitizenshipId);
    const dispatch = useDispatch();
    const [ citizenshipMenuOpen, setCitizenshipMenuOpen ] = useState(false);
    const { selectedServices } = useSelector(getSelectedService);
    const hasCatalogInternationalPrice = useSelector(getHasInternationalPriceInCatalog);

    useEffect(() => {
        setPartialText(true);
    }, [ selectedServices, _selectedService ]);

    const handleCitizenshipSelect = (id) => {
        dispatch(selectCitizenship(id));
        setCitizenshipMenuOpen(false);
        saveCitizenship(id);
    };

    const description = selectedServices?.description?.trim() || _selectedService?.description?.trim();
    const belarusPrice = selectedServices?.price ?? _selectedService?.price;
    const internationalPrice = selectedServices?.internationalPrice ?? _selectedService?.internationalPrice;
    const {
        price,
        citizenshipLabel,
        hasCitizenshipSelector,
    } = getPriceDisplay({
        belarusPrice,
        internationalPrice,
        citizenship,
        hasCatalogInternationalPrice,
    });
    const selectedCitizenship = CITIZENSHIP_OPTIONS.find(({ id }) => id === citizenship);
    return (description || price != null) ? (
        <div className={styles()}>
            {price != null && (
                <>
                    <div className={styles('price-header')}>
                        <div className={styles('price-label')}>
                            Стоимость*
                        </div>
                        {hasCitizenshipSelector && (
                            <CitizenshipControl
                                citizenship={citizenship}
                                citizenshipMenuOpen={citizenshipMenuOpen}
                                selectedCitizenship={selectedCitizenship}
                                onCitizenshipSelect={handleCitizenshipSelect}
                                onMenuToggle={() => setCitizenshipMenuOpen(prev => !prev)}
                                onMenuClose={() => setCitizenshipMenuOpen(false)}
                            />
                        )}
                    </div>
                    <ServicePrice price={price} citizenshipLabel={citizenshipLabel} />
                </>
            )}
            {description ? (
                <>
                    <div className={styles('header')}>Описание</div>
                    <div className={styles('text')}>
                        <div className={styles('content', { partialText })}>
                            {partialText
                                ? `${description?.trim()
                                    .substring(0, SHORT_TEXT_LENGTH)}${
                                    description?.length > SHORT_TEXT_LENGTH ? '...' : ''
                                }`
                                : String(description)
                                    .trim()}
                        </div>
                        {description.length > SHORT_TEXT_LENGTH && (
                            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                            <p className={styles('link')} onClick={() => setPartialText(prev => !prev)}>
                                Показать {partialText ? 'больше' : 'меньше'}
                                <i className={styles('arrow', partialText ? { down: true } : { up: true })}></i>
                            </p>
                        )}
                    </div>
                </>
            ) : null}
        </div>
    ) : null;
};
